import { useEffect, useState } from "react";
import axios from "axios";
import { Bot, Copy, Check, Sparkles, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = window.location.origin + "/api";

/**
 * Pannello admin: connettore MCP unico per Claude e ChatGPT.
 * SEC-002: il segreto NON è più nell'URL. L'URL è statico (/api/mcp) e
 * l'autenticazione avviene tramite header `Authorization: Bearer <segreto>`.
 */
export default function McpConnectorPanel() {
  const [info, setInfo] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedHeader, setCopiedHeader] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/mcp-connector-info`, { withCredentials: true })
      .then((res) => setInfo(res.data))
      .catch(() => setInfo({ enabled: false, path: null }));
  }, []);

  const url = info?.url || (info?.path ? `${window.location.origin}${info.path}` : null);
  const headerValue = info?.header_value || "";
  const isOauth = !!info?.oauth;

  const copyText = async (text, setter, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success(`${label} copiato`);
      setTimeout(() => setter(false), 2000);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="mcp-connector-panel">
      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
        <Bot className="w-4 h-4 text-indigo-500" /> Connettore MCP (Claude &amp; ChatGPT)
      </h3>
      <p className="text-[11px] text-slate-500 mt-1 mb-3">
        Collega Claude o ChatGPT al gestionale: statistiche dashboard,
        appuntamenti, incassi, listini e feedback in sola lettura. Per
        privacy i dati identificativi dei pazienti NON sono esposti.
      </p>

      {info === null && <p className="text-xs text-slate-400">Caricamento…</p>}

      {info?.enabled && url && (
        <>
          {/* URL del connettore */}
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">URL connettore</label>
          <div className="flex items-center gap-2 mt-1">
            <code
              className="flex-1 text-[10px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 truncate"
              title={url}
              data-testid="mcp-connector-url"
            >
              {url}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={() => copyText(url, setCopiedUrl, "URL")}
              className="rounded-xl h-8 w-8 p-0 shrink-0"
              data-testid="mcp-copy-url-btn"
            >
              {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>

          {isOauth ? (
            <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="mcp-oauth-badge">
              <KeyRound className="w-3 h-3" /> Login sicuro integrato (OAuth): basta incollare l'URL e fare <b>Accedi</b> — nessun header da copiare.
            </div>
          ) : (
            <>
              {/* Header di autenticazione legacy (contiene il segreto) */}
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-3 flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> Header di autenticazione
              </label>
              <div className="flex items-center gap-2 mt-1">
                <code
                  className="flex-1 text-[10px] bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 truncate"
                  title={`Authorization: ${headerValue}`}
                  data-testid="mcp-connector-header"
                >
                  Authorization: {headerValue}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copyText(headerValue, setCopiedHeader, "Header")}
                  className="rounded-xl h-8 w-8 p-0 shrink-0"
                  data-testid="mcp-copy-header-btn"
                >
                  {copiedHeader ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </>
          )}

          {/* ── Sezione Claude ─────────────────────────────────────── */}
          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3" data-testid="mcp-claude-section">
            <p className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1.5">
              <Bot className="w-3.5 h-3.5" /> Claude (claude.ai / Desktop)
            </p>
            {isOauth ? (
              <ol className="text-[11px] text-slate-600 mt-1.5 space-y-0.5 list-decimal pl-4">
                <li>Impostazioni → <b>Connettori</b> → "Aggiungi connettore personalizzato"</li>
                <li>Incolla l'<b>URL</b> qui sopra e salva</li>
                <li>Clicca <b>Connetti / Accedi</b>: si apre la pagina di login dello studio</li>
                <li>Accedi con il tuo account <b>admin</b> e autorizza l'accesso in sola lettura</li>
                <li>Chiedi es. "quanto abbiamo fatturato questo mese?"</li>
              </ol>
            ) : (
              <ol className="text-[11px] text-slate-600 mt-1.5 space-y-0.5 list-decimal pl-4">
                <li>Impostazioni → <b>Connettori</b> → "Aggiungi connettore personalizzato"</li>
                <li>Incolla l'<b>URL</b> qui sopra</li>
                <li>Nel campo <b>Request headers</b> aggiungi: nome <code>Authorization</code>, valore <code>{headerValue || "Bearer …"}</code></li>
                <li>Salva, attiva il connettore e chiedi es. "quanto abbiamo fatturato questo mese?"</li>
              </ol>
            )}
          </div>

          {/* ── Sezione ChatGPT ────────────────────────────────────── */}
          <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="mcp-chatgpt-section">
            <p className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> ChatGPT (Pro / Business / Enterprise)
            </p>
            <ol className="text-[11px] text-slate-600 mt-1.5 space-y-0.5 list-decimal pl-4">
              <li>Impostazioni → <b>Connettori</b> → abilita "<b>Developer Mode</b>"</li>
              <li>"Crea" → tipo <b>MCP</b>, incolla l'<b>URL</b> qui sopra</li>
              <li>Autenticazione: <b>OAuth</b> (consigliato) e fai "Accedi" con l'account admin{isOauth ? "" : <>, oppure header <code>Authorization</code> = <code>{headerValue || "Bearer …"}</code></>}</li>
              <li>Attiva il connettore (Deep Research o Apps &amp; Connectors) e chiedi es. "cerca il prezzo dell'implantologia"</li>
            </ol>
          </div>

          <p className="text-[10px] text-amber-600 mt-3">
            ⚠ L'accesso è in <b>sola lettura</b> e su dati anonimi/aggregati. Autorizza solo da account admin dello studio.
          </p>
        </>
      )}

      {info && !info.enabled && (
        <p className="text-xs text-rose-600" data-testid="mcp-disabled-msg">
          Disabilitato: manca MCP_PATH_SECRET nella configurazione del backend.
        </p>
      )}
    </div>
  );
}
